import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineSpinner } from "@/components/common/Loading";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setError(""); setLoading(true);
    try { await api.post("/auth/reset-password", { token, password }); setDone(true); }
    catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  if (!token) return <AuthLayout title="Invalid link"><p className="text-sm text-slate-600">This reset link is invalid.</p></AuthLayout>;

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You can now sign in with your new password.">
        <div className="flex flex-col items-center text-center py-4" data-testid="reset-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-3" />
          <Button onClick={() => navigate("/login")} className="mt-2 bg-[#006090] hover:bg-[#004E75]">Go to sign in</Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-rose-600" data-testid="auth-error">{error}</p>}
        <div>
          <Label className="text-xs font-semibold text-slate-700">New password</Label>
          <Input type="password" value={password} required data-testid="reset-password"
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="At least 8 characters" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Confirm password</Label>
          <Input type="password" value={confirm} required data-testid="reset-confirm"
            onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
        </div>
        <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-[#006090] hover:bg-[#004E75]">
          {loading ? <InlineSpinner /> : "Update password"}
        </Button>
        <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-slate-700">Back to sign in</Link>
      </form>
    </AuthLayout>
  );
}
